import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Truck, Phone, Lock, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const OperatorLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    password: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.phone || !formData.password) {
      toast({
        title: 'Missing Fields',
        description: 'Please enter phone and password',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/login', {
        phone: formData.phone,
        password: formData.password
      });

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Check if user is an approved operator
        try {
          const operatorRes = await api.get('/operators/me');
          if (operatorRes.data.operator?.approvalStatus === 'approved') {
            toast({
              title: 'Welcome back!',
              description: 'Redirecting to your dashboard'
            });
            navigate('/operator/dashboard');
          } else if (operatorRes.data.operator?.approvalStatus === 'pending') {
            toast({
              title: 'Application Pending',
              description: 'Your operator application is still under review',
              variant: 'destructive'
            });
          } else {
            toast({
              title: 'Not an Operator',
              description: 'Please apply to become a cargo operator first',
              variant: 'destructive'
            });
            navigate('/operator/apply');
          }
        } catch (error) {
          // No operator profile, redirect to apply
          navigate('/operator/apply');
        }
      }
    } catch (error) {
      toast({
        title: 'Login Failed',
        description: error.response?.data?.detail || 'Invalid credentials',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Truck className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Operator Portal</h1>
          <p className="text-gray-600 mt-1">Tekstar Logistics</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <Label>Phone Number</Label>
            <div className="relative mt-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+260..."
                className="pl-10"
                data-testid="operator-phone"
              />
            </div>
          </div>

          <div>
            <Label>Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter password"
                className="pl-10 pr-10"
                data-testid="operator-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
            data-testid="operator-login-btn"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        <div className="mt-6 text-center space-y-3">
          <p className="text-sm text-gray-600">
            Not registered yet?{' '}
            <button 
              onClick={() => navigate('/operator/apply')}
              className="text-orange-600 font-medium hover:underline"
            >
              Apply as Operator
            </button>
          </p>
          
          <button 
            onClick={() => navigate('/')}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </button>
        </div>
      </Card>
    </div>
  );
};

export default OperatorLogin;
